/**
 * Who submits the card form, without the caller knowing which processor rendered it.
 *
 * Thirteen call sites reached for the DOM directly:
 *
 *     const component = document.querySelector("rainforest-payment");
 *     if (component) (component as any).submit();
 *
 * That works only because Rainforest's SDK happens to expose a custom element
 * with a `submit()` method. Stripe has no element to query: its surface is a
 * React component holding an Elements instance, and submitting means calling
 * `stripe.confirmPayment(...)` with state the DOM cannot see. A registry of
 * *callbacks* is the smallest thing that covers both.
 *
 * It also fixes two bugs the querySelector form has independently of Stripe:
 *
 * - `document.querySelector` returns the *first* matching element. A page with
 *   two surfaces mounted — a saved-card dialog over a checkout — submits
 *   whichever one React happened to render first, which is not necessarily the
 *   one the user is looking at.
 * - `if (component)` silently does nothing when the element is missing, so a
 *   surface that failed to mount reads as a click that did not happen. Callers
 *   here get `false` back and can say so.
 */

export interface PaymentSurfaceHandle {
  /**
   * Hand the entered card details to the SDK.
   *
   * Deliberately fire-and-forget. Every one of the thirteen call sites already
   * works this way: the outcome arrives later through the surface's own
   * callbacks - `approved`, `declined`, `error` on Rainforest; the equivalent
   * promise resolution on Stripe - not as a return value. Making this
   * synchronous keeps the call sites unchanged in shape, which matters when
   * several of them sit inside non-async JSX handlers.
   */
  submit: () => void;
}

/**
 * A stack, not a single slot: dialogs open over pages that already have a
 * surface, and the one on top is the one the user is looking at. Unregistering
 * removes by identity rather than popping, because unmount order is not
 * guaranteed to be the reverse of mount order.
 */
const surfaces: PaymentSurfaceHandle[] = [];

export function registerPaymentSurface(
  handle: PaymentSurfaceHandle,
): () => void {
  surfaces.push(handle);
  return () => {
    const index = surfaces.lastIndexOf(handle);
    if (index !== -1) surfaces.splice(index, 1);
  };
}

export const hasActivePaymentSurface = (): boolean => surfaces.length > 0;

/**
 * Submit the surface the user is looking at.
 *
 * Returns false when there is none, so a caller can say "the card form is not
 * ready" rather than leaving a spinner running forever - which is what the
 * `if (component)` guard silently did.
 */
export function submitActivePaymentSurface(): boolean {
  const active = surfaces[surfaces.length - 1];
  if (!active) return false;
  active.submit();
  return true;
}

/** Test helper. Never call this from application code. */
export function __resetPaymentSurfaces(): void {
  surfaces.length = 0;
}
